import { Icon } from '@iconify/react';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useEffect, useRef, useState } from 'react';
import {
  ACCEPTED_FILE_TYPES,
  DEFAULT_BUTTON_CLASS,
  DEFAULT_BUTTON_TEXT,
  DEFAULT_PREVIEW_TEXT,
  PREVIEW_POSITION,
  UPLOAD_MODE,
} from './fileUpload.constants';
import {
  getButtonLabel,
  handleFileChange,
  hasUploadedFile,
  normalizeFileValue,
} from './fileUpload.utils';

export default function FileUploadField({
  value,
  onChange,
  trigger,
  name,
  sx,
  autoUpload,
  disabled,
  buttonClass = DEFAULT_BUTTON_CLASS,
  buttonIcon,
  buttonText = DEFAULT_BUTTON_TEXT,
  showPreview = false,
  previewPosition = PREVIEW_POSITION.AFTER,
  previewText = DEFAULT_PREVIEW_TEXT,
  previewLinkProps,
  accept = ACCEPTED_FILE_TYPES,
}: any) {
  const inputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [uploading, setUploading] = useState(false);

  const normalizedValue = normalizeFileValue(value);
  const mode = autoUpload ? UPLOAD_MODE.AUTO : UPLOAD_MODE.MANUAL;

  let previewUrl = normalizedValue.url || null;

  if (!previewUrl && normalizedValue.file instanceof File) {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = URL.createObjectURL(normalizedValue.file);
    previewUrl = previewUrlRef.current;
  }

  const { sx: previewSx, ...previewProps } = previewLinkProps || {};

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const renderPreview = () =>
    showPreview && !uploading && previewUrl ? (
      <MuiLink
        href={previewUrl}
        target='_blank'
        rel='noopener noreferrer'
        sx={{
          textDecoration: 'underline',
          color: 'grey.500',
          '&:visited': { color: 'grey.500' },
          ...previewSx,
        }}
        {...previewProps}
      >
        {previewText}
      </MuiLink>
    ) : null;

  return (
    <Stack direction='row' gap={2} alignItems='center' sx={sx}>
      {previewPosition === PREVIEW_POSITION.BEFORE && renderPreview()}

      <button
        type='button'
        className={buttonClass}
        disabled={uploading || disabled}
        onClick={() => inputRef.current?.click()}
      >
        {buttonIcon && <Icon icon={buttonIcon} width={18} />}
        {uploading && !buttonIcon ? (
          <CircularProgress size={18} />
        ) : (
          <span>
            {getButtonLabel({
              uploading,
              hasValue: hasUploadedFile(normalizedValue),
              buttonText,
            })}
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type='file'
        hidden
        accept={accept}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';

          await handleFileChange({
            file,
            mode,
            onChange,
            trigger,
            name,
            setUploading,
          });
        }}
      />

      {previewPosition === PREVIEW_POSITION.AFTER && renderPreview()}
    </Stack>
  );
}
